import {useEffect} from 'react'
import {
  ChatBskyConvoDefs,
  type ChatBskyGroupApproveJoinRequest,
  type ChatBskyGroupRejectJoinRequest,
} from '@atproto/api'
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'

import {DM_SERVICE_HEADERS} from '#/lib/constants'
import {logger} from '#/logger'
import {useMessagesEventBus} from '#/state/messages/events'
import {createQueryKey} from '#/state/queries/util'
import {useAgent} from '#/state/session'
import {STALE} from '..'
import {RQKEY as CONVO_KEY} from './conversation'

const listJoinRequestsQueryKeyRoot = 'list-join-requests'

export const createListJoinRequestsQueryKey = (args: {convoId: string}) =>
  createQueryKey(listJoinRequestsQueryKeyRoot, args)

export function useListJoinRequestsQuery({
  convoId,
  enabled,
}: {
  convoId: string | undefined
  enabled?: boolean
}) {
  const agent = useAgent()
  const queryClient = useQueryClient()
  const messagesBus = useMessagesEventBus()
  const isEnabled = enabled !== false && !!convoId

  useEffect(() => {
    if (!isEnabled || !convoId) return

    return messagesBus.on(
      event => {
        if (event.type !== 'logs') return
        for (const log of event.logs) {
          if (
            ChatBskyConvoDefs.isLogIncomingJoinRequest(log) ||
            ChatBskyConvoDefs.isLogApproveJoinRequest(log) ||
            ChatBskyConvoDefs.isLogRejectJoinRequest(log)
          ) {
            void queryClient.invalidateQueries({
              queryKey: createListJoinRequestsQueryKey({convoId}),
            })
            return
          }
        }
      },
      {convoId},
    )
  }, [isEnabled, convoId, messagesBus, queryClient])

  return useInfiniteQuery({
    enabled: isEnabled,
    queryKey: createListJoinRequestsQueryKey({convoId: convoId ?? ''}),
    queryFn: async ({pageParam}) => {
      const {data} = await agent.chat.bsky.group.listJoinRequests(
        {convoId: convoId!, cursor: pageParam, limit: 20},
        {headers: DM_SERVICE_HEADERS},
      )
      return data
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: page => page.cursor,
    staleTime: STALE.MINUTES.ONE,
  })
}

export function useApproveJoinRequestMutation({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: ChatBskyGroupApproveJoinRequest.OutputSchema) => void
  onError?: (error: Error) => void
} = {}) {
  const agent = useAgent()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      convoId,
      member,
    }: {
      convoId: string
      member: string
    }) => {
      const {data} = await agent.chat.bsky.group.approveJoinRequest(
        {convoId, member},
        {headers: DM_SERVICE_HEADERS, encoding: 'application/json'},
      )
      return data
    },
    onSuccess: (data, {convoId}) => {
      queryClient.setQueryData(CONVO_KEY(convoId), data.convo)
      void queryClient.invalidateQueries({
        queryKey: createListJoinRequestsQueryKey({convoId}),
      })
      onSuccess?.(data)
    },
    onError: error => {
      logger.error(error)
      onError?.(error)
    },
  })
}

export function useRejectJoinRequestMutation({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: ChatBskyGroupRejectJoinRequest.OutputSchema) => void
  onError?: (error: Error) => void
} = {}) {
  const agent = useAgent()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      convoId,
      member,
    }: {
      convoId: string
      member: string
    }) => {
      const {data} = await agent.chat.bsky.group.rejectJoinRequest(
        {convoId, member},
        {headers: DM_SERVICE_HEADERS, encoding: 'application/json'},
      )
      return data
    },
    onSuccess: (data, {convoId}) => {
      void queryClient.invalidateQueries({
        queryKey: createListJoinRequestsQueryKey({convoId}),
      })
      onSuccess?.(data)
    },
    onError: error => {
      logger.error(error)
      onError?.(error)
    },
  })
}
